import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Menu,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  Add,
  Close,
  DeleteOutline,
  Edit,
  MessageOutlined,
  MoreVert,
  NotificationsNone,
  Schedule,
  Search,
  Send,
} from '@material-ui/icons';
import CustomTabComponent from 'components/CustomTab';
import api from 'services/api';
import { toast } from 'react-toastify';
import SettingEmail from '../Email/index/index';

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  breadcrumb: { display: 'block', marginBottom: theme.spacing(0.5) },
  title: { marginBottom: theme.spacing(2) },
  pushHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    margin: theme.spacing(0, 0, 3),
    [theme.breakpoints.down('xs')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
      '& button': { marginTop: 0 },
    },
  },
  metricCard: { height: '100%' },
  metricContent: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  metricValue: { lineHeight: 1, marginBottom: theme.spacing(0.75) },
  metricIcon: {
    alignItems: 'center',
    borderRadius: '50%',
    display: 'flex',
    height: 38,
    justifyContent: 'center',
    width: 38,
  },
  metricIconBlue: { backgroundColor: '#e1f1ff', color: theme.palette.primary.main },
  metricIconYellow: { backgroundColor: '#fff1cf', color: '#f3a600' },
  metricIconGreen: { backgroundColor: '#d9f8e4', color: '#23a45d' },
  listCard: { marginTop: theme.spacing(3) },
  listToolbar: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: 64,
    padding: theme.spacing(0, 2),
    [theme.breakpoints.down('xs')]: {
      alignItems: 'stretch',
      flexDirection: 'column',
      padding: theme.spacing(1, 1.5),
    },
  },
  listTabs: { minHeight: 64 },
  searchField: {
    width: 235,
    [theme.breakpoints.down('xs')]: { marginTop: theme.spacing(1), width: '100%' },
  },
  emptyState: { padding: theme.spacing(6, 2), textAlign: 'center' },
  emptyIcon: { marginBottom: theme.spacing(1) },
  dialogPaper: { maxHeight: 'calc(100% - 48px)', maxWidth: 700, width: '100%' },
  dialogTitle: {
    alignItems: 'flex-start',
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(2.5),
  },
  quickTemplates: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
  },
  quickTemplate: {
    background: theme.palette.background.paper,
    border: '1px solid ' + theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    minHeight: 88,
    padding: theme.spacing(1.25),
    textAlign: 'left',
    transition: 'border-color .2s, box-shadow .2s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      boxShadow: '0 2px 8px rgba(0, 0, 0, .1)',
    },
  },
  quickTemplateTitle: {
    display: 'block',
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  dialogContent: {
    display: 'grid',
    gap: theme.spacing(2.5),
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3),
  },
  scheduleRow: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  dialogActions: { padding: theme.spacing(1.5, 3) },
}));

const MetricCard = ({ icon, iconClassName, label, value }) => {
  const classes = useStyles();

  return (
    <Card className={classes.metricCard}>
      <CardContent className={classes.metricContent}>
        <div>
          <Typography className={classes.metricValue} variant="h4">{value}</Typography>
          <Typography color="textSecondary" variant="caption">{label}</Typography>
        </div>
        <div className={`${classes.metricIcon} ${iconClassName}`}>{icon}</div>
      </CardContent>
    </Card>
  );
};

const PUSH_TEMPLATES = [
  {
    id: 'new-course',
    destination: 'course',
    title: '📢 Novo curso disponível',
    body: 'Um novo curso está disponível para você. Acesse agora.',
    color: '#1976d2',
    background: '#edf6ff',
  },
  {
    id: 'training-reminder',
    title: '▶️ Continue de onde parou',
    body: 'Você iniciou um treinamento. Continue de onde parou e avance na sua jornada.',
    color: '#d48a00',
    background: '#fff7e5',
  },
  {
    id: 'certificate-released',
    destination: 'course',
    title: '🎓 Acesse o curso e receba seu certificado',
    body: 'Finalize seu curso e receba seu certificado de conclusão. Acesse agora.',
    color: '#159947',
    background: '#edf9f1',
  },
];

const AUTOMATIC_COMMUNICATIONS = [
  {
    id: 'inactive-user',
    title: 'Lembrete para usuário inativo',
    description: 'Convida o aluno a retomar os estudos após um período sem acesso.',
    schedule: 'Após 7, 14 e 30 dias sem acesso · 08:00',
    channel: 'E-mail',
  },
  {
    id: 'required-course',
    title: 'Curso obrigatório pendente',
    description: 'Lembra o aluno que possui curso ou trilha obrigatória não concluída.',
    schedule: 'A cada 3 dias · 08:30',
    channel: 'Push + E-mail',
  },
  {
    id: 'enrollment-expiration',
    title: 'Matrícula próxima do vencimento',
    description: 'Alerta sobre matrículas individuais que vencem em breve.',
    schedule: '7, 3 e 1 dia antes do vencimento · 07:30',
    channel: 'E-mail',
  },
  {
    id: 'recycling-expiration',
    title: 'Reciclagem próxima do vencimento',
    description: 'Alerta sobre a reciclagem do curso próxima do vencimento.',
    schedule: '7 dias antes do vencimento · 07:50',
    channel: 'E-mail',
  },
];

const CreateNotificationModal = ({ onClose, onCreated, open }) => {
  const classes = useStyles();
  const [scheduled, setScheduled] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [recentCourses, setRecentCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [coursesLoading, setCoursesLoading] = useState(false);

  const handleTemplateClick = async (template) => {
    setSelectedTemplate(template.id);
    setSelectedCourse(null);
    setTitle(template.title);
    setBody(template.body);

    if (template.destination !== 'course') return;

    setCoursesLoading(true);
    try {
      const endpoint = template.id === 'new-course'
        ? '/push-notifications/recent-courses'
        : '/push-notifications/all-courses';
      const response = await api.get(endpoint);
      setRecentCourses(response.data.courses || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Não foi possível carregar os cursos disponíveis.');
    } finally {
      setCoursesLoading(false);
    }
  };

  const handleCourseSelect = (course) => {
    setSelectedCourse(course || null);

    if (course) {
      const message = selectedTemplate === 'certificate-released'
        ? 'Finalize o curso ' + course.name + ' e receba seu certificado de conclusão. Acesse agora.'
        : 'O curso ' + course.name + ' já está disponível. Acesse agora.';
      setBody(message.slice(0, 160));
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() || !body.trim()) {
      toast.warn('Preencha o título e a mensagem da notificação.');
      return;
    }

    if (['new-course', 'certificate-released'].includes(selectedTemplate) && !selectedCourse) {
      toast.warn('Selecione um curso para a notificação.');
      return;
    }

    if (scheduled && !scheduledAt) {
      toast.warn('Informe a data e o horário do agendamento.');
      return;
    }

    setSending(true);

    try {
      const response = await api.post('/push-notifications', {
        title: title.trim(),
        body: body.trim(),
        data: selectedCourse
          ? {
            destination: 'course',
            origin: 'manual',
            id_company_course: selectedCourse.id_company_course,
            id_course: selectedCourse.id_course,
          }
          : { destination: 'app', origin: 'manual' },
        scheduled_at: scheduled ? scheduledAt : null,
      });

      const recipients = response.data?.recipients || 0;
      toast.success(
        scheduled
          ? 'Notificação agendada para ' + recipients + ' dispositivo(s).'
          : 'Notificação enviada para '  + recipients + ' dispositivo(s).'
      );
      onClose();
      onCreated();
    } catch (error) {
      toast.error(
        error.response?.data?.error || 'Não foi possível enviar a notificação.'
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog classes={{ paper: classes.dialogPaper }} fullWidth onClose={onClose} open={open}>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <div>
          <Typography variant="h5">Nova notificação</Typography>
          <Typography color="textSecondary" variant="body2">Envie uma mensagem para os aplicativos.</Typography>
        </div>
        <IconButton aria-label="Fechar" onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent className={classes.dialogContent}>
        <div>
          <Typography color='textSecondary' gutterBottom variant='caption'>Modelos rápidos</Typography>
          <div className={classes.quickTemplates}>
            {PUSH_TEMPLATES.map((template) => (
              <button
                className={classes.quickTemplate}
                key={template.title}
                style={{
                  backgroundColor: template.background,
                  borderColor: template.color + '55',
                  borderTop: '3px solid ' + template.color,
                }}
                onClick={() => handleTemplateClick(template)}
                type='button'
              >
                <Typography className={classes.quickTemplateTitle} style={{ color: template.color }} variant='body2'>{template.title}</Typography>
                <Typography color='textSecondary' variant='caption'>{template.body}</Typography>
              </button>
            ))}
          </div>
        </div>
        {['new-course', 'certificate-released'].includes(selectedTemplate) && (
          <Autocomplete
            getOptionLabel={(course) => course.name || ''}
            loading={coursesLoading}
            loadingText='Carregando cursos...'
            noOptionsText='Nenhum curso disponível'
            onChange={(_, course) => handleCourseSelect(course)}
            options={recentCourses}
            value={selectedCourse}
            renderInput={(params) => (
              <TextField
                {...params}
                label={selectedTemplate === 'new-course' ? 'Curso publicado' : 'Buscar curso disponível'}
                placeholder={selectedTemplate === 'new-course' ? 'Selecione um dos 3 cursos mais recentes' : 'Digite o nome do curso'}
                variant='outlined'
              />
            )}
          />
        )}
        <TextField fullWidth label="Título" onChange={(event) => setTitle(event.target.value)} placeholder="Ex.: Novo curso disponível!" value={title} variant="outlined" />
        <TextField fullWidth helperText={body.length + '/160 caracteres'} inputProps={{ maxLength: 160 }} label="Mensagem" multiline onChange={(event) => setBody(event.target.value)} placeholder="Escreva a mensagem que será enviada..." rows={4} value={body} variant="outlined" />
        <FormControl fullWidth variant="outlined">
          <InputLabel id="push-audience-label">Público</InputLabel>
          <Select defaultValue="all" label="Público" labelId="push-audience-label">
            <MenuItem value="all">Todos os usuários</MenuItem>
          </Select>
        </FormControl>
        {/* <FormControl fullWidth variant="outlined">
          <InputLabel id="push-destination-label">Ao tocar na notificação</InputLabel>
          <Select defaultValue="app" label="Ao tocar na notificação" labelId="push-destination-label">
            <MenuItem value="app">Abrir o aplicativo</MenuItem>
          </Select>
        </FormControl> */}
        <Divider />
        <div className={classes.scheduleRow}>
          <div>
            <Typography variant="subtitle2">Agendar envio</Typography>
            <Typography color="textSecondary" variant="body2">Escolha uma data e horário para enviar.</Typography>
          </div>
          <Switch checked={scheduled} color="primary" onChange={(event) => setScheduled(event.target.checked)} />
        </div>
        {scheduled && <TextField fullWidth InputLabelProps={{ shrink: true }} label="Data e horário" onChange={(event) => setScheduledAt(event.target.value)} type="datetime-local" value={scheduledAt} variant="outlined" />}
      </DialogContent>
      <Divider />
      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} variant="outlined">Cancelar</Button>
        <Button color="primary" disabled={sending} onClick={handleSubmit} startIcon={<Send />} variant="contained">
          {sending ? 'Enviando...' : scheduled ? 'Agendar notificação' : 'Enviar notificação'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const toDateTimeLocal = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const timezoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
};

const EditScheduledNotificationModal = ({ notification, onClose, onSaved }) => {
  const classes = useStyles();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!notification) return;
    setTitle(notification.title || '');
    setBody(notification.body || '');
    setScheduledAt(toDateTimeLocal(notification.scheduled_at));
  }, [notification]);

  const handleSubmit = async () => {
    if (!title.trim() || !body.trim() || !scheduledAt) {
      toast.warn('Preencha o título, a mensagem e a data do agendamento.');
      return;
    }

    setSaving(true);
    try {
      await api.put('/push-notifications/' + notification.batch_id, {
        title: title.trim(),
        body: body.trim(),
        scheduled_at: scheduledAt,
      });
      toast.success('Agendamento atualizado.');
      onSaved();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Não foi possível atualizar o agendamento.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog classes={{ paper: classes.dialogPaper }} fullWidth onClose={onClose} open={Boolean(notification)}>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <div>
          <Typography variant="h5">Editar agendamento</Typography>
          <Typography color="textSecondary" variant="body2">Altere a mensagem ou o horário do envio.</Typography>
        </div>
        <IconButton aria-label="Fechar" onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent className={classes.dialogContent}>
        <TextField fullWidth label="Título" onChange={(event) => setTitle(event.target.value)} value={title} variant="outlined" />
        <TextField fullWidth helperText={body.length + '/160 caracteres'} inputProps={{ maxLength: 160 }} label="Mensagem" multiline onChange={(event) => setBody(event.target.value)} rows={4} value={body} variant="outlined" />
        <TextField fullWidth InputLabelProps={{ shrink: true }} label="Data e horário" onChange={(event) => setScheduledAt(event.target.value)} type="datetime-local" value={scheduledAt} variant="outlined" />
      </DialogContent>
      <Divider />
      <DialogActions className={classes.dialogActions}>
        <Button disabled={saving} onClick={onClose} variant="outlined">Cancelar</Button>
        <Button color="primary" disabled={saving} onClick={handleSubmit} variant="contained">
          {saving ? 'Salvando...' : 'Salvar agendamento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const getStatus = (status) => {
  const values = {
    1: { color: '#159947', label: 'Enviada' },
    2: { color: '#d89100', label: 'Pendente' },
    3: { color: '#d94b4b', label: 'Recusada' },
  };
  return values[Number(status)] || values[2];
};

const NotificationList = ({ loading, notifications, onDelete, onEdit, onSearchChange, onStatusChange, onTabChange, search, statusFilter, tab }) => {
  const classes = useStyles();
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const openActions = (event, notification) => {
    setActionsAnchor(event.currentTarget);
    setSelectedNotification(notification);
  };

  const closeActions = () => {
    setActionsAnchor(null);
    setSelectedNotification(null);
  };
  return (
    <Card className={classes.listCard}>
      <div className={classes.listToolbar}>
        <Tabs className={classes.listTabs} indicatorColor='primary' onChange={(_, value) => onTabChange(value)} textColor='primary' value={tab}>
          <Tab label='Notificações' />
          <Tab label='Automáticas' />
        </Tabs>
        <TextField
          className={classes.searchField}
          InputProps={{ startAdornment: <InputAdornment position='start'><Search fontSize='small' /></InputAdornment> }}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder='Pesquisar comunicação'
          size='small'
          value={search}
          variant='outlined'
        />
        {tab === 0 && (
          <FormControl style={{ marginLeft: 16, minWidth: 150 }}>
            <InputLabel id='push-status-filter-label' shrink>Status da notificação</InputLabel>
            <Select
              displayEmpty
              labelId='push-status-filter-label'
              onChange={(event) => onStatusChange(event.target.value)}
              value={statusFilter}
            >
              <MenuItem value=''>Todos os status</MenuItem>
              <MenuItem value='scheduled'>Agendadas</MenuItem>
            </Select>
          </FormControl>
        )}
      </div>
      <Divider />
      <Table aria-label='Notificações push'>
        <TableHead>
          <TableRow>
            <TableCell>Mensagem</TableCell>
            <TableCell>Público</TableCell>
            <TableCell>Envio</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align='right'>Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {notifications.length > 0 ? notifications.map((notification) => {
            const status = getStatus(notification.status);
            const recipients = Number(notification.recipients);
            const sendingDate = Number(notification.status) === 2 ? notification.scheduled_at : notification.sent_at || notification.created_at;
            return (
              <TableRow key={notification.batch_id}>
                <TableCell>
                  <Typography variant='subtitle2'>{notification.title}</Typography>
                  <Typography color='textSecondary' variant='caption'>{notification.body}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Todos os usuários</Typography>
                  <Typography color='textSecondary' variant='caption'>{recipients + (recipients === 1 ? ' destinatário' : ' destinatários')}</Typography>
                </TableCell>
                <TableCell>{formatDate(sendingDate)}</TableCell>
                <TableCell>
                  <Chip
                    label={status.label}
                    size='small'
                    style={{ backgroundColor: status.color + '1a', color: status.color, fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell align='right'>
                  {Number(notification.status) === 2 && notification.scheduled_at && (
                    <>
                    <IconButton aria-label='Ações do agendamento' onClick={(event) => openActions(event, notification)} size='small'>
                      <MoreVert style={{ fontSize: 19 }} />
                    </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            );
          }) : (
            <TableRow>
              <TableCell className={classes.emptyState} colSpan={5}>
                <NotificationsNone className={classes.emptyIcon} color='disabled' />
                <Typography color='textSecondary' variant='body2'>
                  {loading ? 'Carregando notificações...' : tab === 0 ? 'Nenhuma notificação push enviada ainda.' : 'Nenhuma comunicação automática configurada ainda.'}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Menu
        anchorEl={actionsAnchor}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        keepMounted
        onClose={closeActions}
        open={Boolean(actionsAnchor)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuItem onClick={() => {
          const notification = selectedNotification;
          closeActions();
          if (notification) onEdit(notification);
        }}>
          <Edit style={{ fontSize: 17, marginRight: 8 }} /> Editar agendamento
        </MenuItem>
        <MenuItem onClick={() => {
          const notification = selectedNotification;
          closeActions();
          if (notification) onDelete(notification);
        }} style={{ color: '#d32f2f' }}>
          <DeleteOutline style={{ fontSize: 17, marginRight: 8 }} /> Excluir agendamento
        </MenuItem>
      </Menu>
    </Card>
  );
};

const AutomaticNotificationsList = ({ onTabChange }) => {
  const classes = useStyles();

  return (
    <Card className={classes.listCard}>
      <div className={classes.listToolbar}>
        <Tabs className={classes.listTabs} indicatorColor='primary' onChange={(_, value) => onTabChange(value)} textColor='primary' value={1}>
          <Tab label='Notificações' />
          <Tab label='Automáticas' />
        </Tabs>
      </div>
      <Divider />
      <Table aria-label='Comunicações automáticas'>
        <TableHead>
          <TableRow>
            <TableCell>Automação</TableCell>
            <TableCell>Quando é enviada</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {AUTOMATIC_COMMUNICATIONS.map((automation) => (
            <TableRow key={automation.id}>
              <TableCell>
                <Typography variant='subtitle2'>{automation.title}</Typography>
                <Typography color='textSecondary' variant='caption'>{automation.description}</Typography>
              </TableCell>
              <TableCell>{automation.schedule}</TableCell>
              <TableCell>
                <Chip
                  label='Ativa'
                  size='small'
                  style={{ backgroundColor: '#1599471a', color: '#159947', fontWeight: 600 }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

const PushNotifications = () => {
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [summary, setSummary] = useState({ automatic: 0, scheduled: 0, sent: 0 });
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const loadNotifications = async () => {
    if (tab === 1) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get('/push-notifications', {
        params: {
          limit: 20,
          origin: tab === 0 ? 'manual' : 'automatic',
          search,
          status: tab === 0 ? statusFilter : undefined,
        },
      });
      setNotifications(response.data.notifications || []);
      setSummary(response.data.summary || { automatic: 0, scheduled: 0, sent: 0 });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Não foi possível carregar as notificações.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadNotifications();
  }, [tab, search, statusFilter]);

  const handleDeleteNotification = async (notification) => {
    if (!window.confirm('Excluir este agendamento? Esta ação não pode ser desfeita.')) return;

    try {
      await api.delete('/push-notifications/' + notification.batch_id);
      toast.success('Agendamento excluído.');
      loadNotifications();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Não foi possível excluir o agendamento.');
    }
  };

  return (
    <div>
      <div className={classes.pushHeader}>
        <Typography style={{ marginLeft: 16 }} color='textSecondary' variant='body2'>Crie, agende e acompanhe as notificações enviadas aos aplicativos.</Typography>
        <Button style={{ margin: 16 }} color='primary' onClick={() => setModalOpen(true)} startIcon={<Add />} variant='contained'>Nova notificação</Button>
      </div>
      <Grid style={{ padding: '16px' }} container spacing={3}>
        <Grid item md={4} sm={6} xs={12}><MetricCard icon={<Send fontSize='small' />} iconClassName={classes.metricIconBlue} label='Notificações enviadas' value={summary.sent} /></Grid>
        <Grid item md={4} sm={6} xs={12}><MetricCard icon={<Schedule fontSize='small' />} iconClassName={classes.metricIconYellow} label='Notificações agendadas' value={summary.scheduled} /></Grid>
        <Grid item md={4} sm={6} xs={12}><MetricCard icon={<MessageOutlined fontSize='small' />} iconClassName={classes.metricIconGreen} label='Comunicações automáticas' value={AUTOMATIC_COMMUNICATIONS.length} /></Grid>
      </Grid>
      {tab === 0 ? (
        <NotificationList
          loading={loading}
          notifications={notifications}
          onDelete={handleDeleteNotification}
          onEdit={setEditingNotification}
          onSearchChange={setSearch}
          onStatusChange={setStatusFilter}
          onTabChange={setTab}
          search={search}
          statusFilter={statusFilter}
          tab={tab}
        />
      ) : <AutomaticNotificationsList onTabChange={setTab} />}
      <CreateNotificationModal onClose={() => setModalOpen(false)} onCreated={loadNotifications} open={modalOpen} />
      <EditScheduledNotificationModal
        notification={editingNotification}
        onClose={() => setEditingNotification(null)}
        onSaved={loadNotifications}
      />
    </div>
  );
};

export default () => {
  const classes = useStyles();
  const componentsTab = [
    { label: 'Notificações aplicativo', component: <PushNotifications /> },
    { label: 'E-mail', component: <SettingEmail /> },
  ];

  return (
    <div className={classes.root}>
      <Typography className={classes.breadcrumb} color="textSecondary" variant="caption">Configurações / Comunicação</Typography>
      <Typography className={classes.title} variant="h4">Comunicação</Typography>
      <CustomTabComponent components={componentsTab} />
    </div>
  );
};
